import React, { useEffect } from 'react';
import SEOHead from '@/components/seo/SEOHead';
import { lazy, Suspense } from 'react';
import { Footer } from '@/components/layout/Footer';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

// Lazy load the FAQ component for better performance
const FAQComponent = lazy(() => import('@/components/landing/FAQSection'));

const faqStructuredData = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "Is Meduler free to use?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. Meduler's Starter plan is completely free with no credit card required. It includes a full medication schedule, dual caregiver profiles, offline access, and a calendar view. Meduler Plus unlocks advanced features like caregiver missed-dose alerts and intelligent reminders for $4.99/month."
      }
    },
    {
      "@type": "Question",
      "name": "How secure is my medication data?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Meduler is offline-first — your medication data is stored locally on your device and is never sold to advertisers or third parties. All data transmitted (for optional sync features) uses industry-standard encryption in transit and at rest."
      }
    },
    {
      "@type": "Question",
      "name": "Can I use Meduler to manage medications for someone else?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Absolutely. The free plan includes dual user profiles so a caregiver can manage a loved one's medications alongside their own. Meduler Plus adds shared access for multiple caregivers and missed-dose alerts so you're notified when a dose goes unconfirmed."
      }
    },
    {
      "@type": "Question",
      "name": "What features are included in Meduler Plus?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Meduler Plus includes medication tracking and adherence reports, intelligent reminders with advanced customization, caregiver missed-dose alerts, shared access for care teams, and early access to new features. Plus is listed at $4.99/month."
      }
    },
    {
      "@type": "Question",
      "name": "Does Meduler work without an internet connection?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. Meduler is offline-first. Your entire medication schedule, push reminders, and history work without Wi-Fi or mobile data. This makes it reliable for travel, rural areas, or any situation with limited connectivity."
      }
    },
    {
      "@type": "Question",
      "name": "Does Meduler check for drug interactions?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "No. Meduler is a medication scheduling and adherence app, not a clinical decision tool. It does not check for drug-drug or drug-food interactions. Always consult your pharmacist or prescriber about potential interactions."
      }
    },
    {
      "@type": "Question",
      "name": "How do I set up medication reminders in Meduler?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "After adding a medication, tap the reminder settings to enable push notifications. You can set the reminder time, tone, and snooze duration for each medication individually. Most users complete setup in under 60 seconds."
      }
    },
    {
      "@type": "Question",
      "name": "Can I cancel Meduler Plus at any time?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. You can cancel your Plus subscription at any time. You'll retain Plus access until the end of your current billing period, after which your account reverts to the free Starter plan. Your data remains intact."
      }
    }
  ]
};

const FAQPage: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  useEffect(() => {
    if (typeof window !== 'undefined' && 'gtag' in window) {
      (window as any).gtag('event', 'page_view', {
        page_title: 'Meduler FAQ',
        page_path: window.location.pathname,
      });
    }
    
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((registration) => {
        registration.update().catch(() => {});
      });
    }
  }, []);

  const FAQSkeleton = () => (
    <div className="py-20 px-4 bg-gradient-to-b from-purple-50 to-white">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-center mb-4">
          <div className="h-14 w-14 bg-purple-100 rounded-full animate-pulse"></div>
        </div>
        
        <div className="text-center mb-10">
          <div className="h-8 w-40 mx-auto bg-gray-200 rounded-full animate-pulse mb-4"></div>
          <div className="h-10 bg-gray-200 rounded animate-pulse mb-5 max-w-md mx-auto"></div>
          <div className="h-6 bg-gray-200 rounded animate-pulse mb-4 max-w-lg mx-auto"></div>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-purple-100">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="border-b border-purple-100 last:border-b-0">
              <div className="p-5">
                <div className="flex justify-between items-center">
                  <div className="h-6 bg-gray-200 rounded animate-pulse w-3/4"></div>
                  <div className="h-6 w-6 rounded-full bg-gray-200 animate-pulse"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        <div className="text-center mt-8">
          <div className="h-6 w-48 bg-gray-200 rounded animate-pulse mx-auto"></div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <SEOHead
        title="FAQ — Common Questions About Meduler"
        description="Find answers to common questions about Meduler's medication management app, including features, privacy, pricing, and Plus subscription benefits."
        keywords="meduler faq, medication app questions, medication reminder help, meduler plus cost, medication adherence app help"
        canonicalUrl="https://www.meduler.com/faq"
        image="https://www.meduler.com/social/og-image.png"
        structuredData={faqStructuredData}
      />

      <main className="min-h-screen bg-gradient-to-b from-white via-purple-50 to-white">
        {/* Hero Section */}
        <section className="pt-4 md:pt-6 pb-12 px-4 md:px-8">
          <motion.div 
            className="max-w-4xl mx-auto text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="inline-block px-3 py-1 mb-4 rounded-full bg-purple-100 border border-purple-200">
              <p className="text-sm font-medium text-purple-700">Help & Support</p>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              What Are the Most Common <span className="text-purple-600">Questions</span> About Meduler?
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Meduler is a free, offline-first medication reminder and adherence tracker — here are answers to the questions we hear most from patients and caregivers.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="/" 
                className="inline-flex items-center justify-center px-6 py-3 bg-purple-600 text-white font-medium rounded-lg shadow-sm hover:bg-purple-700 transition-colors"
              >
                Start for Free
              </a>
              <a 
                href="mailto:support@meduler.com" 
                className="inline-flex items-center justify-center px-6 py-3 bg-white border border-purple-200 text-purple-600 font-medium rounded-lg shadow-sm hover:bg-purple-50 transition-colors"
              >
                Contact Support
              </a>
            </div>
          </motion.div>
        </section>

        {/* Expanded FAQ Section with all Q&As */}
        <section className="py-8 px-4 md:px-8" aria-labelledby="faq-full-heading">
          <div className="max-w-3xl mx-auto">
            <h2 id="faq-full-heading" className="text-2xl font-bold text-center text-gray-900 mb-8">All Questions</h2>
            <div className="space-y-4">
              {[
                { q: "Is Meduler free to use?", a: "Yes. Meduler's Starter plan is completely free with no credit card required. It includes a full medication schedule, dual caregiver profiles, offline access, and a calendar view. Meduler Plus unlocks advanced features like caregiver missed-dose alerts and intelligent reminders for $4.99/month." },
                { q: "How secure is my medication data?", a: "Meduler is offline-first — your medication data is stored locally on your device and is never sold to advertisers or third parties. All transmitted data uses industry-standard encryption in transit and at rest." },
                { q: "Can I use Meduler to manage medications for someone else?", a: "Yes. The free plan includes dual user profiles so a caregiver can manage a loved one's medications alongside their own. Meduler Plus adds shared access for multiple caregivers and missed-dose alerts." },
                { q: "What features are included in Meduler Plus?", a: "Meduler Plus includes medication tracking, adherence reports, intelligent reminders, caregiver missed-dose alerts, shared access for care teams, and early access to new features — listed at $4.99/month." },
                { q: "Does Meduler work without an internet connection?", a: "Yes. Meduler is offline-first. Your schedule, reminders, and history all work without Wi-Fi or mobile data — ideal for travel or areas with limited connectivity." },
                { q: "Does Meduler check for drug interactions?", a: "No. Meduler is a scheduling and adherence app, not a clinical decision tool. It does not check for drug-drug or drug-food interactions. Always consult your pharmacist or prescriber." },
                { q: "How do I set up medication reminders in Meduler?", a: "After adding a medication, tap the reminder settings to enable push notifications. Set the time, tone, and snooze duration per medication. Most users complete setup in under 60 seconds." },
                { q: "Can I cancel Meduler Plus at any time?", a: "Yes. Cancel anytime. You retain Plus access until the end of your billing period, then revert to the free plan. Your data stays intact." },
              ].map((faq, i) => (
                <details key={i} className="rounded-xl border border-purple-100 bg-white shadow-sm group">
                  <summary className="p-5 font-semibold text-gray-800 cursor-pointer list-none flex justify-between items-center">
                    {faq.q}
                    <span className="text-purple-400 group-open:rotate-180 transition-transform">▼</span>
                  </summary>
                  <div className="px-5 pb-5 text-gray-600 text-sm leading-relaxed">{faq.a}</div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Content - Lazy Loaded (existing FAQ component) */}
        <motion.div 
          ref={ref}
          initial={{ opacity: 0, y: 10 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="w-full"
        >
          <Suspense fallback={<FAQSkeleton />}>
            <FAQComponent />
          </Suspense>
        </motion.div>

        {/* Still Have Questions Section */}
        <section className="py-16 px-4 md:px-8 bg-gradient-to-r from-purple-600 to-violet-700 text-white">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Still have questions?</h2>
            <p className="text-xl mb-8 text-purple-100">
              Our support team is ready to help you with any questions about Meduler.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="mailto:support@meduler.com" 
                className="inline-flex items-center justify-center px-8 py-3 bg-white text-purple-600 font-medium rounded-lg shadow-lg hover:bg-purple-50 transition-colors"
              >
                Email Support
              </a>
              <a 
                href="/plus" 
                className="inline-flex items-center justify-center px-8 py-3 bg-purple-800 border border-white text-white font-medium rounded-lg shadow-lg hover:bg-purple-900 transition-colors"
              >
                Explore Meduler Plus
              </a>
            </div>
          </div>
        </section>

        <Footer />
      </main>
    </>
  );
};

export default FAQPage;
